import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import { JSONContent } from "@tiptap/core";

import BannerStatusIcon from "assets/customIcons/status/BannerStatusIcon";
import Autocomplete from "components/DesignComponents/Autocomplete";
import Switch, { SWITCH_SIZES } from "components/DesignComponents/Switch";
import HighlightedOption from "components/HighlightedOption";
import { BannerLevelOption, DROPDOWN_SIZES, useBannerLevels } from "shared";

import Editor from "./Editor";
import { BannerProps } from "./types";

const Banner = ({ data, isLoading, onSubmit }: BannerProps) => {
    const { t } = useTranslation();
    const { LEVEL_OPTIONS, activeLevelOption } = useBannerLevels(data.level);

    const parseMessage = (rawMessage: string): JSONContent | null => {
        if (!rawMessage) return null;

        const tryParseJson = (str: string) => {
            try {
                return JSON.parse(str);
            } catch {
                return undefined;
            }
        };

        const convertToDoc = (message: string) => ({
            type: "doc",
            content: [
                {
                    type: "paragraph",
                    content: [{ type: "text", text: message }]
                }
            ]
        });

        const parsed = tryParseJson(rawMessage);

        if (parsed?.type === "doc") return parsed as JSONContent;

        return convertToDoc(parsed !== undefined ? String(parsed) : rawMessage);
    };

    const [levelInputValue, setLevelInputValue] = useState("");
    const [level, setLevel] = useState(() => activeLevelOption);

    const [message, setMessage] = useState<JSONContent | null>(() =>
        parseMessage(data.message)
    );

    const [isActive, setIsActive] = useState(data.isActive);

    useEffect(() => {
        onSubmit({
            id: data.id,
            level: level.label,
            message: message === null ? "" : JSON.stringify(message),
            isActive
        });
        // eslint-disable-next-line
    }, [data.id, level, message, isActive]);

    const handleMessage = (newMessage: JSONContent | null) =>
        setMessage(newMessage);

    const { background, border } = level;

    return (
        <>
            <Autocomplete
                size={DROPDOWN_SIZES.Medium}
                labelLeft={t("Banner##banner importance")}
                textFieldParams={{
                    placeholder: t("Dialog##select")
                }}
                options={LEVEL_OPTIONS}
                value={level}
                readOnly={isLoading}
                isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                }
                iconLeft={
                    <BannerStatusIcon
                        bgColor={background}
                        borderColor={border}
                    />
                }
                disableClearable
                getOptionLabel={option => t(`Banner##${option.label}`)}
                renderOption={(props, value: BannerLevelOption) => {
                    const { key, ...restProps } = props;
                    const { label, background, border } = value;

                    return (
                        <li
                            {...restProps}
                            key={key}
                            css={css({
                                padding: "12px 16px !important"
                            })}
                        >
                            <span
                                css={css({
                                    display: "flex",
                                    marginRight: "8px"
                                })}
                            >
                                <BannerStatusIcon
                                    bgColor={background}
                                    borderColor={border}
                                />
                            </span>

                            <HighlightedOption
                                text={t(`Banner##${label}`)}
                                query={levelInputValue}
                            />
                        </li>
                    );
                }}
                onInputChange={(_, newInputValue) =>
                    setLevelInputValue(newInputValue)
                }
                onChange={(_, value) => setLevel(value)}
                onClose={() => setLevelInputValue("")}
            />

            <Editor
                content={message}
                isDisabled={isLoading}
                onMessageChange={handleMessage}
            />

            <Switch
                size={SWITCH_SIZES.Medium}
                labelRight={t("Banner##activate banner")}
                checked={isActive}
                disabled={isLoading}
                onChange={(_, isChecked) => setIsActive(isChecked)}
                customStyles={{
                    margin: "16px 0 8px 0"
                }}
            />
        </>
    );
};

export default Banner;
